import { Checkbox } from '@foam/buoy/src/components/Checkbox/Checkbox';
import { cn } from '@foam/buoy/src/lib/utils';

import { getReadableMetricValue } from './utils';

export interface NewMetricCardProps {
  isDisabled?: boolean;
  isChecked?: boolean;
  onChange?: (v: boolean) => void;
  metricTitle: string;
  metricSubtitle?: string;
  metricValue: number | string | undefined;
  metricTransformValue?: 'percentage' | 'number' | 'string';
  themed?: boolean;
}

export const NewMetricCard: React.FC<NewMetricCardProps> = ({
  metricTitle,
  metricSubtitle,
  metricValue,
  isDisabled = false,
  isChecked = undefined,
  onChange,
  metricTransformValue = 'number',
  themed = false,
}) => {
  const isSelectable = typeof isChecked === 'boolean';
  const Component = isSelectable ? 'label' : 'div';

  return (
    <Component
      className={cn(
        'relative flex flex-col rounded-12 border px-4 py-3',
        themed
          ? 'border-[var(--media-kit-background-alpha)] bg-[var(--media-kit-background-primary)]'
          : cn(
              'bg-card-background-color-default',
              !isDisabled && 'shadow-ground',
            ),
        isSelectable && 'cursor-pointer',
        !themed &&
          (isChecked ? 'border-blue-500' : 'border-card-border-color-default'),
      )}
    >
      {isSelectable ? (
        <Checkbox
          checked={isChecked}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={isDisabled}
          aria-label={`Select ${metricTitle}`}
          className="absolute right-2 top-2"
        />
      ) : null}
      <div className="flex items-center justify-between">
        <p
          data-testid="metric-title"
          className={cn(
            'text-16 font-500',
            themed ? 'text-[var(--media-kit-text-subtle)]' : 'text-neutral-700',
          )}
        >
          {metricTitle}
        </p>
        <p
          data-testid="metric-value"
          className={cn(
            'text-16 font-500',
            themed ? 'text-[var(--media-kit-text-base)]' : 'text-neutral-700',
            isSelectable && 'pr-6',
          )}
        >
          {getReadableMetricValue(metricValue, metricTransformValue)}
        </p>
      </div>
      {metricSubtitle ? (
        <p
          data-testid="metric-subtitle"
          className={cn(
            'text-12 font-400',
            themed ? 'text-[var(--media-kit-text-subtle)]' : 'text-neutral-300',
          )}
        >
          {metricSubtitle}
        </p>
      ) : null}
    </Component>
  );
};
